import React, { useEffect, useMemo, useState } from 'react';
import InstallerLayout from './InstallerLayout';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getJob, getCurrentInstallerId, getBidsByJob, upsertBid, getInstallerProfile } from '../../lib/data';

export default function JobDetails({ user }) {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const installerId = getCurrentInstallerId(user);
  const job = getJob(jobId);
  const [existingBid, setExistingBid] = useState(null);

  useEffect(() => {
    const mine = getBidsByJob(jobId).find(b => b.installerId === installerId);
    setExistingBid(mine || null);
  }, [jobId, installerId]);

  const [form, setForm] = useState({
    amountTotal: '',
    pricePerSqFt: '',
    materials: 'Asphalt Shingles',
    warrantyYears: 10,
    timelineDays: 5,
    startDateEstimate: '',
    notes: '',
    validityUntil: ''
  });

  useEffect(() => {
    if (existingBid) {
      setForm({
        amountTotal: existingBid.amountTotal,
        pricePerSqFt: existingBid.pricePerSqFt,
        materials: existingBid.materials,
        warrantyYears: existingBid.warrantyYears,
        timelineDays: existingBid.timelineDays,
        startDateEstimate: existingBid.startDateEstimate || '',
        notes: existingBid.notes || '',
        validityUntil: existingBid.validityUntil || ''
      });
    }
  }, [existingBid]);

  if (!job) {
    return (
      <InstallerLayout>
        <div className="bg-white rounded-xl p-6">Job not found.</div>
      </InstallerLayout>
    );
  }

  const profile = getInstallerProfile(installerId);

  const computeDerived = () => {
    const total = Number(form.amountTotal) || 0;
    const ppsf = Number(form.pricePerSqFt) || (job.squareFeet ? (total / job.squareFeet) : 0);
    const finalTotal = total || Math.round((Number(form.pricePerSqFt) || 0) * job.squareFeet);
    return { ppsf, finalTotal };
  };
  const derived = computeDerived();

  const onSubmit = (e) => {
    e.preventDefault();
    const payload = {
      id: existingBid?.id,
      jobId: job.id,
      installerId,
      amountTotal: derived.finalTotal,
      pricePerSqFt: Number(derived.ppsf.toFixed(2)),
      materials: form.materials,
      warrantyYears: Number(form.warrantyYears),
      timelineDays: Number(form.timelineDays),
      startDateEstimate: form.startDateEstimate || null,
      notes: form.notes,
      validityUntil: form.validityUntil || null
    };
    const saved = upsertBid(payload);
    // After submit, take to compare page for this job
    navigate(`/compare-bids?jobId=${job.id}`, { replace: false });
  };

  return (
    <InstallerLayout>
      <div className="space-y-6">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm text-gray-500">Job</div>
              <div className="text-xl font-semibold text-gray-900" data-testid="job-title">{job.title}</div>
              <div className="text-sm text-gray-600">{job.address}</div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">{job.squareFeet.toLocaleString()} sq ft</div>
              <div className="text-xs text-gray-500">{job.roofType} • {job.confidence}</div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="font-semibold text-gray-900 mb-4">Submit Your Bid</div>
            <form onSubmit={onSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Total Amount ($)</label>
                  <input data-testid="bid-amount" type="number" min="0" value={form.amountTotal} onChange={e=>setForm(f=>({...f, amountTotal: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-gray-300" placeholder="e.g. 12,500" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Price per sq ft ($)</label>
                  <input data-testid="bid-ppsf" type="number" min="0" step="0.01" value={form.pricePerSqFt} onChange={e=>setForm(f=>({...f, pricePerSqFt: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-gray-300" placeholder="e.g. 4.50" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Warranty (years)</label>
                  <input data-testid="bid-warranty" type="number" min="0" value={form.warrantyYears} onChange={e=>setForm(f=>({...f, warrantyYears: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-gray-300" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Timeline (days)</label>
                  <input data-testid="bid-timeline" type="number" min="0" value={form.timelineDays} onChange={e=>setForm(f=>({...f, timelineDays: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-gray-300" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Est. Start Date</label>
                  <input data-testid="bid-start-date" type="date" value={form.startDateEstimate} onChange={e=>setForm(f=>({...f, startDateEstimate: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-gray-300" />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Valid Until</label>
                  <input data-testid="bid-validity" type="date" value={form.validityUntil} onChange={e=>setForm(f=>({...f, validityUntil: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-gray-300" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Materials</label>
                <select data-testid="bid-materials" value={form.materials} onChange={e=>setForm(f=>({...f, materials: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-gray-300">
                  <option>Asphalt Shingles</option>
                  <option>Architectural Shingles</option>
                  <option>Metal Roofing</option>
                  <option>TPO</option>
                  <option>EPDM</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Notes</label>
                <textarea data-testid="bid-notes" value={form.notes} onChange={e=>setForm(f=>({...f, notes: e.target.value}))} rows={3} className="w-full px-3 py-2 rounded-lg border border-gray-300" placeholder="Add any details or exclusions"></textarea>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                <div>Computed: ${derived.finalTotal.toLocaleString()} total • ${derived.ppsf.toFixed(2)}/sq ft</div>
                <div className="text-xs text-gray-500">Based on {job.squareFeet.toLocaleString()} sq ft</div>
              </div>

              <button data-testid="submit-bid-button" type="submit" className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700">{existingBid ? 'Update Bid' : 'Submit Bid'}</button>
            </form>
          </div>

          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <div className="font-semibold text-gray-900 mb-4">Job Details</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Roof Type</div><div className="font-semibold">{job.roofType}</div></div>
              <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Total Area</div><div className="font-semibold">{job.squareFeet.toLocaleString()} sq ft</div></div>
              <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Pitched Area</div><div className="font-semibold">{(job.pitchedArea||Math.round(job.squareFeet*0.8)).toLocaleString()} sq ft</div></div>
              <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Flat Area</div><div className="font-semibold">{(job.flatArea||Math.round(job.squareFeet*0.2)).toLocaleString()} sq ft</div></div>
              <div className="bg-gray-50 rounded-lg p-3 col-span-2"><div className="text-gray-500">Detection</div><div className="font-semibold">{job.detectionMethod} • {job.confidence}</div></div>
            </div>

            <div className="mt-6">
              <div className="text-sm text-gray-600 mb-2">Preview homeowner comparison</div>
              <Link to={`/compare-bids?jobId=${job.id}`} data-testid="preview-compare" className="inline-block px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm">Open Compare Bids</Link>
            </div>

            <div className="mt-6 text-xs text-gray-500">
              Submitting a bid will make it visible to the homeowner on their Compare Bids page.
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="font-semibold text-gray-900 mb-2">Your Company Info (shown with your bid)</div>
          <div className="text-sm text-gray-700">{profile.companyName} • {profile.contactName} • {profile.phone}</div>
          <div className="text-xs text-gray-500">Edit in <Link to="/installer/profile" className="text-blue-600">Profile</Link></div>
        </div>
      </div>
    </InstallerLayout>
  );
}
